import { useState } from "react";
import {
  validateEmail,
  validatePassword,
  validateRequired,
  validateGender,
  validateNigerianPhoneNumber,
} from "../services/validation";

interface ValidationRules {
  [key: string]: (value: any) => boolean;
}

interface ValidationMessages {
  [key: string]: string;
}

export function useFormValidation() {
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const validateField = (
    value: any,
    rules: ValidationRules,
    messages: ValidationMessages
  ): { [key: string]: string } => {
    const currentErrors: { [key: string]: string } = {};

    Object.keys(rules).forEach((rule) => {
      if (!rules[rule](value)) {
        currentErrors[rule] = messages[rule];
      }
    });

    return currentErrors;
  };

  const validateForm = (
    data: any,
    validationSchema: {
      [key: string]: { rules: ValidationRules; messages: ValidationMessages };
    }
  ): boolean => {
    const currentErrors: { [key: string]: string } = {};

    Object.keys(validationSchema).forEach((field) => {
      const fieldErrors = validateField(
        data[field],
        validationSchema[field].rules,
        validationSchema[field].messages
      );
      Object.assign(currentErrors, fieldErrors);
    });

    setErrors(currentErrors);
    return Object.keys(currentErrors).length === 0;
  };

  const clearErrors = () => {
    setErrors({});
  };

  const setFieldError = (field: string, message: string) => {
    setErrors((prev) => ({ ...prev, [field]: message }));
  };

  return {
    errors,
    validateForm,
    validateField,
    clearErrors,
    setFieldError,
  };
}

// Common validation schemas
export const commonValidations = {
  email: {
    rules: {
      required: (value: string) => validateRequired(value),
      email: (value: string) => validateEmail(value),
    },
    messages: {
      required: "Email is required",
      email: "Valid email is required",
    },
  },
  password: {
    rules: {
      required: (value: string) => validateRequired(value),
      password: (value: string) => validatePassword(value),
    },
    messages: {
      required: "Password is required",
      password: "Password must be at least 8 characters",
    },
  },
  required: {
    rules: {
      required: (value: string) => validateRequired(value),
    },
    messages: {
      required: "This field is required",
    },
  },
  gender: {
    rules: {
      required: (value: number) => validateGender(value),
    },
    messages: {
      required: "Gender is required",
    },
  },
  phoneNumber: {
    rules: {
      required: (value: string) => validateRequired(value),
      phone: (value: string) => validateNigerianPhoneNumber(value),
    },
    messages: {
      required: "Phone number is required",
      phone: "Phone number not in correct format",
    },
  },
};
